import { useEffect, useState } from 'react'
import { Card, Form, Row, Col, Button, Alert } from 'react-bootstrap'
import { toast } from 'react-toastify'
import {
  useBlockAdminUser,
  useUnblockAdminUser,
  useUpdateAdminUser
} from 'api/hooks/use-admin-user'
import { errorMessage } from 'admin/shared/errors'
import UserStatusBadge from 'shared/badges/user-status-badge'

const UserBlockCard = ({ user }) => {
  const userId = user?.id
  // `auth0_state` is the live status resolved against Auth0; fall back to the
  // locally mirrored `blocked` flag if it isn't present for some reason.
  const state = user?.auth0_state || (user?.blocked ? 'blocked' : 'active')
  const isBlocked = state === 'blocked'
  const noAuth0Account = state === 'deleted'

  const [scheduledFor, setScheduledFor] = useState(user?.block_scheduled_for ?? '')
  const [errorMsg, setErrorMsg] = useState(null)

  // Resync the scheduled date when the server record actually changes.
  useEffect(() => {
    setScheduledFor(user?.block_scheduled_for ?? '')
  }, [user?.updated_at, user?.block_scheduled_for])

  const onError = (fallback) => (err) => {
    const msg = errorMessage(err, fallback)
    setErrorMsg(msg)
    toast.error(msg)
  }

  const { mutate: blockUser, isLoading: blocking } = useBlockAdminUser({
    userId,
    options: {
      onSuccess: () => { setErrorMsg(null); toast.success('User blocked') },
      onError: onError('Failed to block user')
    }
  })

  const { mutate: unblockUser, isLoading: unblocking } = useUnblockAdminUser({
    userId,
    options: {
      onSuccess: () => { setErrorMsg(null); toast.success('User unblocked') },
      onError: onError('Failed to unblock user')
    }
  })

  const { mutate: saveUser, isLoading: savingSchedule } = useUpdateAdminUser({
    userId,
    options: {
      onSuccess: () => { setErrorMsg(null); toast.success('Auto-block date updated') },
      onError: onError('Failed to update auto-block date')
    }
  })

  const saveSchedule = () => saveUser({ block_scheduled_for: scheduledFor || null })
  const clearSchedule = () => {
    setScheduledFor('')
    saveUser({ block_scheduled_for: null })
  }

  const scheduleUnchanged = (scheduledFor || '') === (user?.block_scheduled_for ?? '')

  return (
    <Card className="mb-4">
      <Card.Header className="d-flex justify-content-between align-items-center">
        <h5 className="mb-0">Account Status</h5>
        <UserStatusBadge state={state} />
      </Card.Header>
      <Card.Body>
        {errorMsg && <Alert variant="danger">{errorMsg}</Alert>}

        {noAuth0Account ? (
          <p className="text-muted mb-0">
            This user has no Auth0 account, so blocking does not apply. An account
            is created in Auth0 when the user is granted app access.
          </p>
        ) : (
          <>
            <Row className="align-items-end">
              <Col md={6}>
                <p className="mb-2">
                  This user is currently{' '}
                  <strong>{isBlocked ? 'blocked' : 'active'}</strong> in Auth0.
                  {isBlocked
                    ? ' They cannot log in until unblocked.'
                    : ' They can log in normally.'}
                </p>
                {isBlocked ? (
                  <Button
                    variant="success"
                    onClick={() => unblockUser()}
                    disabled={unblocking}
                  >
                    {unblocking ? 'Unblocking…' : 'Unblock User'}
                  </Button>
                ) : (
                  <Button
                    variant="danger"
                    onClick={() => blockUser()}
                    disabled={blocking}
                  >
                    {blocking ? 'Blocking…' : 'Block User'}
                  </Button>
                )}
              </Col>

              {!isBlocked && (
                <Col md={6}>
                  <Form.Group controlId="user-block-scheduled-for">
                    <Form.Label>Auto-block on</Form.Label>
                    <div className="d-flex gap-2">
                      <Form.Control
                        type="date"
                        value={scheduledFor || ''}
                        onChange={(e) => setScheduledFor(e.target.value)}
                      />
                      <Button
                        variant="primary"
                        onClick={saveSchedule}
                        disabled={savingSchedule || scheduleUnchanged}
                      >
                        Save
                      </Button>
                      {user?.block_scheduled_for && (
                        <Button
                          variant="light"
                          className="text-primary"
                          onClick={clearSchedule}
                          disabled={savingSchedule}
                        >
                          Clear
                        </Button>
                      )}
                    </div>
                    <Form.Text className="text-muted">
                      The user is automatically blocked on this date.
                    </Form.Text>
                  </Form.Group>
                </Col>
              )}
            </Row>

            {!isBlocked && user?.block_scheduled_for && (
              <Alert variant="warning" className="mt-3 mb-0">
                Scheduled to be auto-blocked on{' '}
                {new Date(`${user.block_scheduled_for}T00:00:00`).toLocaleDateString()}.
              </Alert>
            )}
          </>
        )}
      </Card.Body>
    </Card>
  )
}

export default UserBlockCard
